import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import DBModal from "../../../../component/global/DBModal";
import DBButton from "../../../../component/global/DBButton";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import DBSelect from "../../../../component/global/DBSelect";
import DBSwitch from "../../../../component/global/DBSwitch";
import DBTextArea from "../../../../component/global/DBTextArea";
import DBInput from "../../../../component/global/DBInput";
import { ToastContext } from "../../../../component/global/ToastContext";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    SETTING_FORMS_AI_APPLY,
    SETTING_FORMS_AI_GENERATE,
    SETTING_FORMS_USER_GROUP,
    SETTING_ITEMS_LIST,
    SURVEY_AI_GENERATE,
} from "../../../../helperData/ApiAddress";
import {
    CriteriaStructureEditor,
    validateFormStructure,
} from "../../../../modules/form-builder";
import { SURVEY_SUBJECT } from "../../../surveys/constants";
import {
    systemUserGroupRequestParams,
    systemUserGroupSelectOptions,
} from "../../../../helperData/systemUserGroups";
import { translateServerDetail } from "../../../surveys/translateServerDetail";
import { AiCoinHint, isInsufficientCoins, useAiCoinGate } from "../../../../features/ai-billing/AiCoinGate";

const EXPERTISE_OPTIONS = [
    { value: "general", label: t("ai.form.expertise.general") },
    {
        value: "semi_specialized",
        label: t("ai.form.expertise.semi.specialized"),
    },
    { value: "specialized", label: t("ai.form.expertise.specialized") },
];

const RESPONSE_STYLE_OPTIONS = [
    { value: "mixed", label: t("ai.form.response.style.mixed") },
    { value: "rating", label: t("ai.form.response.style.rating") },
    { value: "text", label: t("ai.form.response.style.text") },
];

const CRITERIA_COUNT_OPTIONS = [3, 5, 8, 10, 12].map((count) => ({
    value: count,
    label: t("ai.form.criteria.count.value", { count }),
}));

const defaultAnswers = () => ({
    goal: "",
    audience: "",
    user_group: null,
    topics: "",
    exclusions: "",
    expertise_level: "general",
    criteria_count: 5,
    response_style: "mixed",
    active: true,
    subject_type: SURVEY_SUBJECT.FREE,
    subject_title: "",
    item: null,
});

/**
 * Shared AI form wizard.
 * Feedback mode (default): generates and applies a feedback Form for the
 * given itemType, then calls onSuccess(form).
 * Survey mode (variant="survey"): only generates an editable structure and
 * returns it through onApplyStructure({ structure, surveyForm }) so the
 * caller can persist it together with the survey itself.
 */
export default function AIFormModal({
    show,
    onHide,
    itemType,
    onSuccess,
    variant = "feedback",
    onApplyStructure,
    personal = false,
}) {
    const isSurvey = variant === "survey";
    const toast = useContext(ToastContext);
    const [step, setStep] = useState(1);
    const [answers, setAnswers] = useState(defaultAnswers());
    const [structure, setStructure] = useState(null);
    const [stepError, setStepError] = useState("");
    const [userGroups, setUserGroups] = useState([]);
    const [items, setItems] = useState([]);

    const [getUserGroups] = useRequest(SETTING_FORMS_USER_GROUP, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [generateForm, generateLoading] = useRequest(
        isSurvey ? SURVEY_AI_GENERATE : SETTING_FORMS_AI_GENERATE,
        "POST"
    );
    const [applyForm, applyLoading] = useRequest(
        SETTING_FORMS_AI_APPLY,
        "POST"
    );
    const { confirmAndRun, openFromError, gate } = useAiCoinGate();

    useEffect(() => {
        if (show) {
            setStep(1);
            setAnswers(defaultAnswers());
            setStructure(null);
            setStepError("");
            if (!isSurvey) {
                getUserGroups(systemUserGroupRequestParams())
                    .then((r) => {
                        setUserGroups(systemUserGroupSelectOptions(r));
                    })
                    .catch(() => {
                        toast(t("ai.form.user.groups.error"), "danger");
                    });
            } else if (!personal) {
                getItems()
                    .then((r) => {
                        const list = Array.isArray(r) ? r : r.results || [];
                        setItems(
                            list.map((item) => ({
                                value: item.id,
                                label: item.name,
                            }))
                        );
                    })
                    .catch(() => setItems([]));
            } else {
                setItems([]);
            }
        }
    }, [show]);

    const totalSteps = 5;

    const validateStep = () => {
        if (step === 1) {
            if (isSurvey) {
                if (personal || answers.subject_type === SURVEY_SUBJECT.FREE) {
                    if (!answers.subject_title.trim()) {
                        return t("survey.form.subject.title.required");
                    }
                } else if (!answers.item) {
                    return t("survey.form.subject.item.required");
                }
            }
            if (!answers.goal.trim()) {
                return t("ai.form.required");
            }
        }
        if (step === 2) {
            if (!answers.audience.trim()) {
                return t("ai.form.required");
            }
        }
        if (step === 3 && !answers.topics.trim()) {
            return t("ai.form.required");
        }
        return "";
    };

    const buildGeneratePayload = () => {
        const base = {
            goal: answers.goal,
            audience: answers.audience,
            topics: answers.topics,
            exclusions: answers.exclusions,
            expertise_level: answers.expertise_level,
            criteria_count: answers.criteria_count,
            response_style: answers.response_style,
        };
        if (isSurvey) {
            return {
                ...base,
                subject_title:
                    answers.subject_type === SURVEY_SUBJECT.FREE
                        ? answers.subject_title.trim()
                        : "",
                item:
                    personal || answers.subject_type !== SURVEY_SUBJECT.ITEM
                        ? null
                        : answers.item,
                ...(personal ? { personal: true } : {}),
            };
        }
        return {
            ...base,
            item_type: itemType?.id ?? itemType,
            user_group: answers.user_group || null,
            active: answers.active,
        };
    };

    const requestStructure = () => {
        setStepError("");
        confirmAndRun("form_structure", () => {
            generateForm(buildGeneratePayload())
                .then((result) => {
                    setStructure(result.form);
                    setStep(totalSteps + 1);
                })
                .catch((e) => {
                    if (isInsufficientCoins(e)) {
                        openFromError(e);
                        return;
                    }
                    const detail = isSurvey
                        ? translateServerDetail(e.response?.data?.detail, t)
                        : null;
                    toast(
                        detail || t("ai.form.generate.error"),
                        "danger"
                    );
                });
        });
    };

    const handleNext = () => {
        const error = validateStep();
        if (error) {
            setStepError(error);
            return;
        }
        setStepError("");
        if (step === totalSteps) {
            requestStructure();
        } else {
            setStep(step + 1);
        }
    };

    const applyStructure = () => {
        const structureError = validateFormStructure(structure);
        if (structureError) {
            toast(structureError, "danger");
            return;
        }
        if (isSurvey) {
            onApplyStructure?.({
                structure,
                surveyForm: {
                    subject_type: personal
                        ? SURVEY_SUBJECT.FREE
                        : answers.subject_type,
                    subject_title: answers.subject_title,
                    item: personal ? null : answers.item,
                    access_mode: personal ? 1 : undefined,
                },
            });
            onHide?.();
            return;
        }
        applyForm({
            item_type: itemType?.id ?? itemType,
            user_group: answers.user_group || null,
            active: answers.active,
            form: structure,
        })
            .then((r) => {
                onHide?.();
                onSuccess?.(r.form);
                if (r.active_conflict) {
                    toast(t("ai.form.save.active.conflict"), "warning");
                } else {
                    toast(
                        t("ai.form.save.success", {
                            count: r.created_criteria_count,
                        }),
                        "success"
                    );
                }
            })
            .catch(() => {
                toast(t("ai.form.save.error"), "danger");
            });
    };

    const renderProgress = () => (
        <div className="ai-progress">
            <span>{t("ai.form.step", { step })}</span>
            <div role="progressbar" aria-valuenow={step} aria-valuemin={1} aria-valuemax={totalSteps}>
                {Array.from({ length: totalSteps }, (_, index) => (
                    <i key={index} className={index < step ? "active" : ""} />
                ))}
            </div>
        </div>
    );

    const renderSurveySubjectFields = () => (
        <div className="ai-step-grid">
            <DBFieldBlock title={t("survey.form.subject.type")} required>
                <DBSelect
                    size="large"
                    value={answers.subject_type}
                    onChange={(value) =>
                        setAnswers({
                            ...answers,
                            subject_type: value,
                            subject_title: "",
                            item: null,
                        })
                    }
                    options={
                        personal
                            ? [
                                  {
                                      value: SURVEY_SUBJECT.FREE,
                                      label: t(
                                          "survey.form.subject.type.free"
                                      ),
                                  },
                              ]
                            : [
                                  {
                                      value: SURVEY_SUBJECT.FREE,
                                      label: t(
                                          "survey.form.subject.type.free"
                                      ),
                                  },
                                  {
                                      value: SURVEY_SUBJECT.ITEM,
                                      label: t(
                                          "survey.form.subject.type.item"
                                      ),
                                  },
                              ]
                    }
                />
            </DBFieldBlock>
            {answers.subject_type === SURVEY_SUBJECT.FREE ? (
                <DBFieldBlock
                    title={t("survey.form.subject.title")}
                    required
                >
                    <DBInput
                        size="large"
                        value={answers.subject_title}
                        placeholder={t("survey.form.subject.title")}
                        autoComplete="off"
                        onChange={(e) =>
                            setAnswers({
                                ...answers,
                                subject_title: e.target.value,
                            })
                        }
                    />
                </DBFieldBlock>
            ) : (
                <DBFieldBlock
                    title={t("survey.form.subject.item")}
                    required
                >
                    <DBSelect
                        size="large"
                        showSearch
                        optionFilterProp="label"
                        placeholder={t("form.select")}
                        value={answers.item}
                        onChange={(value) =>
                            setAnswers({ ...answers, item: value })
                        }
                        options={items}
                    />
                </DBFieldBlock>
            )}
        </div>
    );

    const renderStep = () => {
        switch (step) {
            case 1:
                return (
                    <div className="ai-question">
                        <div className="ai-question-fields">
                            {isSurvey && renderSurveySubjectFields()}
                            <DBFieldBlock title={t("ai.form.q1")} required>
                                <DBTextArea
                                    rows={4}
                                    value={answers.goal}
                                    placeholder={t("ai.form.q1.placeholder")}
                                    onChange={(e) =>
                                        setAnswers({
                                            ...answers,
                                            goal: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                        </div>
                    </div>
                );
            case 2:
                return (
                    <div className="ai-question">
                        <div className="ai-question-fields">
                            <DBFieldBlock title={t("ai.form.q2")} required>
                                <DBTextArea
                                    rows={4}
                                    value={answers.audience}
                                    placeholder={t("ai.form.q2.placeholder")}
                                    onChange={(e) =>
                                        setAnswers({
                                            ...answers,
                                            audience: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            {!isSurvey && (
                                <div className="ai-step-grid">
                                    <DBFieldBlock title={t("form.user.group")}>
                                        <DBSelect
                                            allowClear
                                            size="large"
                                            placeholder={t("form.select")}
                                            value={answers.user_group}
                                            onChange={(value) =>
                                                setAnswers({
                                                    ...answers,
                                                    user_group: value ?? null,
                                                })
                                            }
                                            options={userGroups}
                                        />
                                    </DBFieldBlock>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 3:
                return (
                    <div className="ai-question">
                        <div className="ai-question-fields">
                            <div className="ai-step-grid">
                                <DBFieldBlock
                                    title={t("ai.form.q3")}
                                    required
                                >
                                    <DBTextArea
                                        rows={4}
                                        value={answers.topics}
                                        placeholder={t(
                                            "ai.form.q3.placeholder"
                                        )}
                                        onChange={(e) =>
                                            setAnswers({
                                                ...answers,
                                                topics: e.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                                <DBFieldBlock title={t("ai.form.exclusions")}>
                                    <DBTextArea
                                        rows={4}
                                        value={answers.exclusions}
                                        placeholder={t(
                                            "ai.form.exclusions.placeholder"
                                        )}
                                        onChange={(e) =>
                                            setAnswers({
                                                ...answers,
                                                exclusions: e.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                            </div>
                        </div>
                    </div>
                );
            case 4:
                return (
                    <div className="ai-question">
                        <div className="ai-question-fields">
                            <div className="ai-step-grid">
                                <DBFieldBlock title={t("ai.form.q4")} required>
                                    <DBSelect
                                        size="large"
                                        value={answers.expertise_level}
                                        onChange={(value) =>
                                            setAnswers({
                                                ...answers,
                                                expertise_level: value,
                                            })
                                        }
                                        options={EXPERTISE_OPTIONS}
                                    />
                                </DBFieldBlock>
                                <DBFieldBlock
                                    title={t("ai.form.criteria.count")}
                                >
                                    <DBSelect
                                        size="large"
                                        value={answers.criteria_count}
                                        onChange={(value) =>
                                            setAnswers({
                                                ...answers,
                                                criteria_count: value,
                                            })
                                        }
                                        options={CRITERIA_COUNT_OPTIONS}
                                    />
                                </DBFieldBlock>
                            </div>
                            <p className="ai-form-help">
                                {t(
                                    `ai.form.expertise.${answers.expertise_level}.help`
                                )}
                            </p>
                        </div>
                    </div>
                );
            case 5:
                return (
                    <div className="ai-question">
                        <div className="ai-question-fields">
                            <div className="ai-step-grid">
                                <DBFieldBlock
                                    title={t("ai.form.response.style")}
                                    span={2}
                                >
                                    <DBSelect
                                        size="large"
                                        value={answers.response_style}
                                        onChange={(value) =>
                                            setAnswers({
                                                ...answers,
                                                response_style: value,
                                            })
                                        }
                                        options={RESPONSE_STYLE_OPTIONS}
                                    />
                                </DBFieldBlock>
                            </div>
                            {!isSurvey && (
                                <div className="ai-form-toggles">
                                    <label>
                                        <DBSwitch
                                            checked={answers.active}
                                            onChange={(value) =>
                                                setAnswers({
                                                    ...answers,
                                                    active: value,
                                                })
                                            }
                                        />
                                        <span>{t("form.active")}</span>
                                    </label>
                                </div>
                            )}
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
        <DBModal
            permanent
            show={show}
            minWidth={720}
            onHide={onHide}
            className="ai-form-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    step > totalSteps
                        ? t("ai.form.preview.title")
                        : t("ai.form.title")
                }
            />
            <DBModal.Content>
                {step <= totalSteps ? (
                    <>
                        {renderProgress()}
                        {stepError && (
                            <p className="ai-error" role="alert">
                                {stepError}
                            </p>
                        )}
                        {renderStep()}
                    </>
                ) : (
                    <div className="ai-form-preview">
                        <h3>{t("ai.form.preview.title")}</h3>
                        <p>{t("ai.form.preview.description")}</p>
                        <DBFieldBlock title={t("form.name")} required>
                            <DBInput
                                size="large"
                                value={structure?.name || ""}
                                onChange={(e) =>
                                    setStructure({
                                        ...structure,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </DBFieldBlock>
                        {structure && (
                            <CriteriaStructureEditor
                                structure={structure}
                                onChange={setStructure}
                                hideName
                            />
                        )}
                    </div>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                {step <= totalSteps ? (
                    <>
                        <DBButton
                            caption={
                                step === totalSteps ? (
                                    <>
                                        {t("ai.form.generate")}
                                        <AiCoinHint operation="form_structure" />
                                    </>
                                ) : (
                                    t("ai.form.next")
                                )
                            }
                            onClick={handleNext}
                            loading={generateLoading}
                        />
                        {step > 1 && (
                            <DBButton
                                caption={t("ai.form.previous")}
                                type="secondary"
                                onClick={() => setStep(step - 1)}
                            />
                        )}
                        <DBButton
                            caption={t("cancel")}
                            type="secondary"
                            onClick={onHide}
                        />
                    </>
                ) : (
                    <>
                        <DBButton
                            caption={t("ai.form.save")}
                            onClick={applyStructure}
                            loading={applyLoading}
                        />
                        <DBButton
                            caption={t("ai.form.regenerate")}
                            type="secondary"
                            onClick={() => setStep(1)}
                        />
                        <DBButton
                            caption={t("cancel")}
                            type="secondary"
                            onClick={onHide}
                        />
                    </>
                )}
            </DBModal.Footer>
        </DBModal>
        {gate}
        </>
    );
}
